/**
 * ShotImageDropConfirm — shared confirm dialog for the drag-an-image-onto-a-shot entry
 * points (board tile, timeline Viewer, timeline ShotBar thumbs). The caller owns the
 * pending-file state; this renders the dialog only.
 *
 * Five variants, one component: 'replace' (default) puts the image on an existing shot,
 * 'create' makes a new shot from it (the board's Add Shot tile), 'create-multi' is that
 * same tile with SEVERAL files — one new shot each — and 'create-scene' / 'create-scene-multi'
 * are the board's Add SCENE button, which creates the scene first and then one shot per image.
 * They differ only in their four strings — forking would mean maintaining the portal, the event
 * isolation and the Escape capture below five times.
 *
 * The two scene variants deliberately carry NO sceneLabel: nothing has been created yet when the
 * dialog asks, so there is no scene number to name. `fileName` stays on the shared base and stays
 * required even for 'create-scene-multi', which renders none of it — exactly as 'create-multi'
 * already does, so the call sites keep one shape.
 *
 * ALWAYS portalled to document.body: the Viewer lives inside .viewerWrapper
 * (position:relative + z-index:0 — a stacking context), where a position:fixed scrim
 * paints UNDER the later shot/audio-lane siblings and loses hit-testing to them; the
 * ShotBar lives in an overflow:hidden lane. Portalling is the only placement that is
 * correct at every call site.
 *
 * Event isolation: React portals re-dispatch events through the REACT tree, so without
 * stopPropagation a scrim tap would bubble into the host's handlers (ShotBar's
 * onBarPointerDown arms the shot-reorder gesture; the Viewer's pen/seek handlers). The
 * scrim therefore swallows pointerdown/pointerup/click. Keep the stopPropagation even
 * though the scrim no longer acts on them itself.
 *
 * 20260818 — deliberately NO outside-tap dismissal, unlike every sibling dialog (which
 * cancels on a scrim onClick). This one is only ever opened by a drag that started in
 * ANOTHER app: on macOS the drop leaves the Previs window unfocused, so the user's next
 * click is the one that activates the window — and it lands on the scrim. It used to
 * cancel on scrim POINTERDOWN, killing the dialog before the button even came back up.
 * Do not restore it: Cancel (autoFocused) and Escape are the dismissal paths.
 *
 * Keyboard: a capture-phase window listener cancels on Escape and keeps the global
 * timeline shortcuts (Space play/pause, arrow scrubbing — window-level listeners that
 * ignore focus) from firing behind a destructive confirm, while letting keys through to
 * the dialog's own focused buttons.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import styles from './ShotImageDropConfirm.module.css';

type Props = {
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
} & (
  | {
      /** Put the image on an EXISTING shot. Default, so every pre-create call site is unchanged. */
      variant?: 'replace';
      /** Display label for the shot (e.g. "3A" or the shot number). */
      shotLabel: string;
      /** True when the shot already carries media (static image / video / animatic) the drop replaces. */
      hasExistingMedia: boolean;
    }
  | {
      /** CREATE a shot from the image (board Add Shot tile) — there is no shot to describe yet. */
      variant: 'create';
      /** Display label for the scene the new shot joins. */
      sceneLabel: string;
    }
  | {
      /** CREATE one shot per dropped image (board Add Shot tile, 2+ files). */
      variant: 'create-multi';
      /** Display label for the scene the new shots join. */
      sceneLabel: string;
      /** How many droppable images the drop carried — always >= 2 (one file takes 'create'). */
      fileCount: number;
    }
  | {
      /** CREATE a whole SCENE from the image (board Add Scene button) — no scene exists to name. */
      variant: 'create-scene';
    }
  | {
      /** CREATE a scene with one shot per dropped image (board Add Scene button, 2+ files). */
      variant: 'create-scene-multi';
      /** How many droppable images the drop carried — always >= 2 (one file takes 'create-scene'). */
      fileCount: number;
    }
);

export function ShotImageDropConfirm(props: Props) {
  const { fileName, onConfirm, onCancel } = props;
  const { t } = useTranslation('common');
  // Narrowed inline at each use: the variants carry different labels, so the discriminant has to
  // be re-tested where the label is read rather than hoisted into a boolean.
  const title = props.variant === 'create-scene-multi'
    ? t('shotImageMultiSceneConfirmTitle', { count: props.fileCount })
    : props.variant === 'create-scene'
      ? t('shotImageNewSceneConfirmTitle')
      : props.variant === 'create-multi'
        ? t('shotImageMultiShotConfirmTitle', { count: props.fileCount })
        : props.variant === 'create'
          ? t('shotImageNewShotConfirmTitle')
          : t('shotImageDropConfirmTitle');
  const body = props.variant === 'create-scene-multi'
    ? t('shotImageMultiSceneConfirmBody', { count: props.fileCount })
    : props.variant === 'create-scene'
      ? t('shotImageNewSceneConfirmBody', { file: fileName })
      : props.variant === 'create-multi'
        ? t('shotImageMultiShotConfirmBody', { count: props.fileCount, scene: props.sceneLabel })
        : props.variant === 'create'
          ? t('shotImageNewShotConfirmBody', { file: fileName, scene: props.sceneLabel })
          : t('shotImageDropConfirmBody', { file: fileName, number: props.shotLabel });
  const note = props.variant === 'create-scene-multi'
    ? t('shotImageMultiSceneConfirmNote')
    : props.variant === 'create-scene'
      ? t('shotImageNewSceneConfirmNote')
      : props.variant === 'create-multi'
        ? t('shotImageMultiShotConfirmNote')
        : props.variant === 'create'
          ? t('shotImageNewShotConfirmNote')
          : props.hasExistingMedia ? t('shotImageDropConfirmReplaceNote') : t('shotImageDropConfirmNote');
  // The two multi actions are handed { count } even where the English copy does not interpolate it:
  // the family's rule is that a translator may need it (and shotImageMultiShotConfirmAction does).
  // Passing it is also why NO key in either family may grow a _one sibling — i18next would switch on
  // it with no code change here. Both multi variants guarantee count >= 2 at the call site.
  const action = props.variant === 'create-scene-multi'
    ? t('shotImageMultiSceneConfirmAction', { count: props.fileCount })
    : props.variant === 'create-scene'
      ? t('shotImageNewSceneConfirmAction')
      : props.variant === 'create-multi'
        ? t('shotImageMultiShotConfirmAction', { count: props.fileCount })
        : props.variant === 'create'
          ? t('shotImageNewShotConfirmAction')
          : t('shotImageDropConfirmAction');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onCancel();
        return;
      }
      const target = e.target as Element | null;
      // Keys aimed at the dialog's own focused buttons pass through (Space/Enter must
      // activate Cancel); anything else is swallowed so useKeyboardShortcuts' window
      // listener can't play/seek/toggle panels behind the modal.
      if (!target?.closest?.('[data-shot-image-drop-confirm]')) {
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  return createPortal(
    <div
      className={styles.scrim}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-label={title}
        data-shot-image-drop-confirm
        onPointerDown={(e) => e.stopPropagation()}
      >
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.body}>{body}</p>
        <p className={styles.note}>{note}</p>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} autoFocus onClick={onCancel}>
            {t('shotSettingsCancel')}
          </button>
          <button type="button" className={styles.confirmBtn} onClick={onConfirm}>
            {action}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
